import { useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { PencilLineIcon } from 'lucide-react';
import { Resolver, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'sonner';
import * as yup from 'yup';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Button } from '@/components/ui/button';
import { FormAutocomplete } from '@/components/ui/custom/Autocomplete/Autocomplete';
import useBrains from '@/hooks/useBrains';
import useIntents from '@/hooks/useIntents';
import { useSendCorrection } from '@/hooks/useSendCorrection';
import i18n from '@/i18n/i18n';
import { submitCorrection } from '@/modules/tryIt/redux/actions';
import { getPermissions } from '@/redux/permissions/selectors';
import { trackEvent } from '@/segment/segment';
import { Intent } from '@/shared/types/intent';
import { actions } from '@/shared/types/permissions';
import { EventName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';
import { AmendPayload } from '@/shared/types/tryIt';
import { slug } from '@/util/validation/patterns';

const intentFormSchema = yup.object({
  intent: yup
    .string()

    .required()
    .matches(slug, { message: () => i18n.t('errors.invalid_intent') }),
});

type Form = yup.InferType<typeof intentFormSchema>;

interface Props {
  userMessage: string;
  possibleMatchedIntents: { intent: string; confidence: number }[];
  brainId: string;
  setAmended: (value: boolean) => void;
  amendPayload: AmendPayload;
  amended: boolean;
}
export const AmendIntent = ({
  possibleMatchedIntents,
  brainId,
  userMessage,
  amendPayload,
  setAmended,
  amended,
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { sendCorrection, isPending: isAmending } = useSendCorrection();
  const { brain } = useBrains(brainId);
  const { brain: draftBrain } = useBrains(brain?.brain_parent_id);

  const {
    intents: hookIntents,
    createNewIntent,
    updateIntentAsync,
    updateStatus,
  } = useIntents(draftBrain?.brain_id);

  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'brains', actions.WRITE)
  );

  const [show, setShow] = useState(false);

  const { control, handleSubmit } = useForm<Form>({
    defaultValues: { intent: '' },
    resolver: yupResolver(intentFormSchema) as Resolver<Form>,
  });

  const selectedIntent = useWatch({ control, name: 'intent' });

  const existingIntent = hookIntents?.find((i) => i.intent === selectedIntent);
  const trainingPhraseExists = !!existingIntent?.expressions?.find(
    (phrase) => phrase === userMessage
  );

  const trackPhraseAdded = () =>
    trackEvent(EventName.TrainingPhraseAdded, {
      session_id: amendPayload.session_id,
      brain_id: brainId,
    });

  const onSubmit = async (data: Form) => {
    const intentName = data.intent;
    // We use hookIntents because its entries have the expressions property
    if (existingIntent) {
      const uniquePhrases = [userMessage, ...existingIntent.expressions];
      try {
        await updateIntentAsync({
          name: existingIntent.intent,
          intent: { expressions: uniquePhrases },
        });
        trackPhraseAdded();
        const intentBelongsToPossibleMatchedIntents = possibleMatchedIntents?.some(
          (intent) => intent.intent === intentName
        );
        if (intentBelongsToPossibleMatchedIntents) {
          const payload = { ...amendPayload, task: 'planner' as const, correction: intentName };

          const result = await sendCorrection(payload);
          if (result.status == 'ok') {
            dispatch(submitCorrection(payload));
          }
          setAmended(true);
        }
        setShow(false);
      } catch {
        // useIntents and useSendCorrection each surface errors via toast; swallow here.
      }
    } else {
      const newIntent: Partial<Intent> = {
        intent: intentName,
        collection: '',
        expressions: [userMessage],
      };
      const result = await createNewIntent(newIntent);
      trackPhraseAdded();
      setShow(false);
      toast.success(t('intent.intent_created', { 0: result?.intent }));
    }
    setAmended(true);
  };

  // Options for FormAutocomplete - considered intents (from possible matches)
  const consideredIntentOptions =
    possibleMatchedIntents
      ?.filter((intent) => intent.intent !== 'offtopics')
      ?.map((intent) => ({
        label: intent.intent,
        value: intent.intent,
        category: t('try_it.considered_intents'),
      })) ?? [];

  // Options for FormAutocomplete - other intents (not in possible matches)
  const otherIntentOptions =
    hookIntents
      ?.filter(
        (intent) =>
          !possibleMatchedIntents?.some((p) => p.intent === intent.intent) &&
          intent.intent !== 'offtopics'
      )
      ?.map((intent) => ({
        label: intent.intent,
        value: intent.intent,
        category: t('try_it.other_intents'),
      })) ?? [];

  // Combined options for the combobox
  const comboboxOptions = [...consideredIntentOptions, ...otherIntentOptions];

  if (amended) {
    return (
      <div className="my-4 flex items-center gap-1 pl-3">
        <span className="text-sm leading-5 font-normal text-successful">{t('try_it.amended')}</span>
        <PencilLineIcon size={16} className="text-successful" />
      </div>
    );
  }

  const handleAmendClick = () => {
    setShow((prev) => !prev);
    trackEvent(EventName.ToggleTrainingPhrasePanel, { session_id: amendPayload.session_id });
  };
  return (
    <div className="pt-1 pb-2 pl-5.5">
      <Button variant="ghost" size="sm" onClick={handleAmendClick} disabled={!canWrite}>
        {t('try_it.amend')}
        <div className="ml-2">
          <PencilLineIcon
            size={16}
            color={canWrite ? 'var(--color-accent)' : 'var(--color-foreground-muted)'}
          />
        </div>
      </Button>
      {show && (
        <div className="mt-2">
          <FormAutocomplete
            label={t('common.intent')}
            control={control}
            options={comboboxOptions}
            name="intent"
            placeholder={t('try_it.select_intent')}
          />
          <span className="text-sm leading-5 font-normal">{t('try_it.message_phrase')}</span>
          <div className="mt-3 flex justify-end gap-2">
            <Button variant="ghost-neutral" size="sm" onClick={() => setShow(false)}>
              {t('common.cancel')}
            </Button>
            <InfoTooltip
              content={trainingPhraseExists ? t('training_phrases.error') : ''}
              open={trainingPhraseExists ? undefined : false}
              showArrow
            >
              <span>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleSubmit(onSubmit)}
                  disabled={trainingPhraseExists}
                  isLoading={isAmending || updateStatus === 'pending'}
                >
                  {t('common.submit')}
                </Button>
              </span>
            </InfoTooltip>
          </div>
        </div>
      )}
    </div>
  );
};
